import * as React from "react";
import { Download, Menu, RefreshCw, Share2, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/Button";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const INSTALL_DISMISSED_KEY = "rideshare-tax:pwa-install-dismissed";
const SHOW_INSTALL_EVENT = "rideshare-tax:show-install-prompt";
const AUTO_INSTALL_IDLE_MS = 1800;

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function getBrowserInstallGuide(hasNativePrompt: boolean) {
  if (hasNativePrompt) {
    return {
      label: "Install",
      title: "Install RideShareTax",
      summary: "Use the app from your home screen for quicker GST updates.",
      steps: ["Tap Install.", "Open it from your home screen next time."],
      icon: Download,
    };
  }

  const userAgent = navigator.userAgent;
  const ios = isIos();
  const android = /android/i.test(userAgent);
  const isIosSafari = ios && /safari/i.test(userAgent) && !/(crios|fxios|edgios)/i.test(userAgent);
  const isIosAltBrowser = ios && !isIosSafari;
  const isFirefox = /firefox|fxios/i.test(userAgent);
  const isSamsung = /samsungbrowser/i.test(userAgent);
  const isEdge = /edg/i.test(userAgent);

  if (isIosSafari) {
    return {
      label: "Got it",
      title: "Add RideShareTax to Home Screen",
      summary: "Safari installs this as a home screen app.",
      steps: ["Tap Share in the Safari toolbar.", "Choose Add to Home Screen."],
      icon: Share2,
    };
  }

  if (isIosAltBrowser) {
    return {
      label: "Got it",
      title: "Add RideShareTax to Home Screen",
      summary: "On iPhone, home screen install is most reliable from Safari.",
      steps: ["Open this page in Safari.", "Tap Share, then Add to Home Screen."],
      icon: Share2,
    };
  }

  if (android && isFirefox) {
    return {
      label: "Got it",
      title: "Install RideShareTax",
      summary: "Firefox uses the browser menu for app installs.",
      steps: ["Tap the browser menu.", "Choose Install or Add to Home Screen."],
      icon: Menu,
    };
  }

  if (android) {
    return {
      label: "Got it",
      title: "Install RideShareTax",
      summary: `${isSamsung ? "Samsung Internet" : isEdge ? "Edge" : "Chrome"} can add this app from its menu.`,
      steps: ["Tap the browser menu.", "Choose Install app or Add to Home screen."],
      icon: Menu,
    };
  }

  return {
    label: "Got it",
    title: "Install RideShareTax",
    summary: "Use your browser install control when it is available.",
    steps: ["Look for the install icon in the address bar.", "Or open the browser menu and choose Install app."],
    icon: Download,
  };
}

export function PwaPrompts({ autoInstallGuide = true }: { autoInstallGuide?: boolean }) {
  const [installPrompt, setInstallPrompt] = React.useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallGuide, setShowInstallGuide] = React.useState(false);
  const [waitingWorker, setWaitingWorker] = React.useState<ServiceWorker | null>(null);
  const installGuide = getBrowserInstallGuide(Boolean(installPrompt));
  const InstallGuideIcon = installGuide.icon;

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isStandalone()) return undefined;

    let fallbackTimer: number | undefined;
    const scheduleFallback = () => {
      if (!autoInstallGuide) return;
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      fallbackTimer = window.setTimeout(() => {
        if (!isStandalone() && window.localStorage.getItem(INSTALL_DISMISSED_KEY) !== "1") {
          setShowInstallGuide(true);
        }
      }, AUTO_INSTALL_IDLE_MS);
    };

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
      if (autoInstallGuide) {
        setShowInstallGuide(true);
      }
    };

    const onManualShow = () => {
      window.localStorage.removeItem(INSTALL_DISMISSED_KEY);
      setShowInstallGuide(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener(SHOW_INSTALL_EVENT, onManualShow);
    window.addEventListener("pointerdown", scheduleFallback, { passive: true });
    window.addEventListener("keydown", scheduleFallback);
    window.addEventListener("scroll", scheduleFallback, { passive: true });
    scheduleFallback();

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener(SHOW_INSTALL_EVENT, onManualShow);
      window.removeEventListener("pointerdown", scheduleFallback);
      window.removeEventListener("keydown", scheduleFallback);
      window.removeEventListener("scroll", scheduleFallback);
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
    };
  }, [autoInstallGuide]);

  React.useEffect(() => {
    if (!("serviceWorker" in navigator) || !import.meta.env.PROD) return undefined;

    let registrationRef: ServiceWorkerRegistration | null = null;
    let updateTimer: number | undefined;
    let requestUpdateHandler: (() => void) | null = null;
    let refreshing = false;

    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.setTimeout(() => {
        window.location.replace(window.location.href);
      }, 250);
    };

    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    navigator.serviceWorker.register("sw.js").then((registration) => {
      registrationRef = registration;

      const showWaitingWorker = () => {
        if (registration.waiting && navigator.serviceWorker.controller) {
          setWaitingWorker(registration.waiting);
        }
      };

      showWaitingWorker();

      registration.addEventListener("updatefound", () => {
        const worker = registration.installing;
        if (!worker) return;
        worker.addEventListener("statechange", () => {
          if (worker.state === "installed" && navigator.serviceWorker.controller) {
            setWaitingWorker(worker);
          }
        });
      });

      const requestUpdate = () => {
        if (document.visibilityState === "visible") {
          void registration.update().then(showWaitingWorker).catch(() => undefined);
        }
      };
      requestUpdateHandler = requestUpdate;

      document.addEventListener("visibilitychange", requestUpdate);
      window.addEventListener("pageshow", requestUpdate);
      updateTimer = window.setTimeout(requestUpdate, 750);
    }).catch(() => undefined);

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      if (updateTimer) window.clearTimeout(updateTimer);
      if (requestUpdateHandler) {
        document.removeEventListener("visibilitychange", requestUpdateHandler);
        window.removeEventListener("pageshow", requestUpdateHandler);
      }
      if (registrationRef) {
        registrationRef.onupdatefound = null;
      }
    };
  }, []);

  async function installApp() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") {
      dismissInstallGuide();
    }
  }

  function dismissInstallGuide() {
    window.localStorage.setItem(INSTALL_DISMISSED_KEY, "1");
    setShowInstallGuide(false);
  }

  function updateApp() {
    waitingWorker?.postMessage({ type: "SKIP_WAITING" });
  }

  return (
    <>
      {showInstallGuide ? (
        <div
          className="fixed bottom-[calc(max(env(safe-area-inset-bottom),0.5rem)+4rem)] left-3 right-3 z-50 mx-auto max-h-[min(72dvh,26rem)] max-w-md overflow-auto rounded-xl border border-border bg-white p-3 shadow-popover sm:bottom-4 sm:left-auto sm:right-6 sm:p-4"
          role="dialog"
          aria-labelledby="install-guide-title"
        >
          <div className="flex items-start gap-3">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-ink">
              <InstallGuideIcon className="size-5" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p id="install-guide-title" className="font-semibold text-ink">{installGuide.title}</p>
                  <p className="mt-1 text-sm leading-6 text-muted">{installGuide.summary}</p>
                </div>
                <button
                  type="button"
                  className="flex size-11 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-surface hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  aria-label="Dismiss install prompt"
                  onClick={dismissInstallGuide}
                >
                  <X className="size-4" aria-hidden="true" />
                </button>
              </div>
              <ol className="mt-3 space-y-1.5 text-sm leading-5 text-muted">
                {installGuide.steps.map((step, index) => (
                  <li key={step} className="flex gap-2">
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-surface text-xs font-semibold text-ink">
                      {index + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                {installPrompt ? (
                  <Button type="button" size="sm" className="min-h-11" onClick={installApp}>
                    <Download className="size-4" aria-hidden="true" />
                    {installGuide.label}
                  </Button>
                ) : (
                  <Button type="button" size="sm" variant="secondary" className="min-h-11" onClick={dismissInstallGuide}>
                    <Smartphone className="size-4" aria-hidden="true" />
                    {installGuide.label}
                  </Button>
                )}
                <Button type="button" size="sm" variant="ghost" className="min-h-11" onClick={dismissInstallGuide}>
                  Not now
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {waitingWorker ? (
        <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+5rem)] left-3 right-3 z-[60] mx-auto max-w-md rounded-xl border border-border bg-white p-4 shadow-popover sm:bottom-4 sm:left-auto sm:right-6">
          <div className="flex items-start gap-3">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-credit text-white">
              <RefreshCw className="size-5" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-ink">Update available</p>
              <p className="mt-1 text-sm leading-6 text-muted">Refresh to use the latest version.</p>
              <div className="mt-3 flex gap-2">
                <Button type="button" size="sm" className="min-h-11" onClick={updateApp}>
                  Update
                </Button>
                <Button type="button" size="sm" variant="ghost" className="min-h-11" onClick={() => setWaitingWorker(null)}>
                  Later
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
